import { useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useAuthStore } from "@/lib/auth-store";
import { logout as logoutApi } from "@/shared/api/auth.api";
import { toastInfo } from "@/lib/toast";
import { AppShellHeader } from "./AppShellHeader";
import { AppShellNavbar } from "./AppShellNavbar";
import { AppFooter } from "./AppFooter";

const HEADER_H = 56; // px · igual a `header={{ height: 56 }}` del AppShell de Mantine
const NAVBAR_EXPANDED = 248; // px
const NAVBAR_RAIL = 72; // px
const SHELL_PADDING = 16; // px · `padding="md"` del AppShell de Mantine
// Espera al SALIR el mouse antes de colapsar (1 s) · da tiempo a volver a
// entrar sin que se contraiga, y evita el "titileo" si el mouse cruza fugazmente
// el borde. Al re-entrar antes de que venza, `cancelarColapso` aborta el colapso.
const COLAPSO_DELAY_MS = 1000;
/**
 * Espera antes de EXPANDIR el sidebar al entrar el mouse: solo se expande si el mouse
 * sigue encima tras este tiempo (evita que se abra al cruzarlo de paso hacia el contenido).
 * Mientras tanto el rail muestra sus tooltips laterales.
 */
const EXPANSION_DELAY_MS = 500;

/**
 * AppShell del portal · reemplazo de `components/layout/app-shell.tsx`
 * (Mantine `<AppShell>`). Sin el componente de Mantine hay que reponer a
 * mano las 3 zonas (header fijo / navbar fijo / main) y las CSS vars que
 * otras piezas ya migradas consumen (`PASO_PAGE_SHELL`, sección Etapa 4
 * de `SolicitudStepper`) — por eso se exponen como `--shell-header-h` /
 * `--shell-navbar-w` / `--shell-padding` en el contenedor raíz, con los
 * MISMOS nombres semánticos que las `--app-shell-*` de Mantine.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sidebar de desktop · 100% por HOVER: el mouse permanece 0.5 s encima → se expande
  // (empuja el contenido), sale → se colapsa al rail. Arranca colapsado (rail). El flicker
  // al empujar se resuelve en las TABLAS (reservan la altura de su scrollbar y
  // calculan su ancho por CSS), no aquí.
  const [desktopExpanded, setDesktopExpanded] = useState(false);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();
  const roles = user?.roles ?? [];
  const permisos = user?.permisos ?? [];

  async function handleLogout() {
    const refreshToken = useAuthStore.getState().refreshToken;
    await logoutApi(refreshToken);
    clear();
    // Purga TODO el caché de TanStack Query: sin esto, al entrar con otra cuenta en
    // la misma pestaña se arrastraban datos de la sesión anterior (bandeja, listas,
    // dashboard) hasta que cada query se refetcheaba.
    queryClient.clear();
    toastInfo({ title: "Sesión cerrada", message: "Hasta luego" });
    void navigate({ to: "/login" as never, replace: true });
  }

  // Colapso al salir el mouse · con una gracia mínima (COLAPSO_DELAY_MS) para no
  // titilar si el mouse cruza fugazmente el borde. El timer se cancela si el
  // mouse vuelve a entrar. Solo desktop (en táctil no hay mouseleave).
  const colapsoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelarColapso = () => {
    if (colapsoTimer.current !== null) {
      clearTimeout(colapsoTimer.current);
      colapsoTimer.current = null;
    }
  };

  const programarColapso = () => {
    cancelarColapso();
    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 768px)").matches) return;
    colapsoTimer.current = setTimeout(() => {
      setDesktopExpanded(false);
      colapsoTimer.current = null;
    }, COLAPSO_DELAY_MS);
  };

  // Expansión diferida · el mouse debe permanecer EXPANSION_DELAY_MS sobre el sidebar;
  // si sale antes, el timer se cancela y el sidebar no se expande.
  const expansionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelarExpansion = () => {
    if (expansionTimer.current !== null) {
      clearTimeout(expansionTimer.current);
      expansionTimer.current = null;
    }
  };

  const programarExpansion = () => {
    cancelarExpansion();
    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 768px)").matches) return;
    expansionTimer.current = setTimeout(() => {
      setDesktopExpanded(true);
      expansionTimer.current = null;
    }, EXPANSION_DELAY_MS);
  };

  // Limpia los timers al desmontar.
  useEffect(
    () => () => {
      cancelarColapso();
      cancelarExpansion();
    },
    [],
  );

  const navbarWidth = desktopExpanded ? NAVBAR_EXPANDED : NAVBAR_RAIL;
  // Expandir es MÁS LENTO y suave (sensación de fade logarítmico · easing expo en
  // globals.css); colapsar también se suaviza pero se mantiene algo más ágil.
  const navbarAnimMs = desktopExpanded ? "1000ms" : "720ms";

  return (
    <div
      className="min-h-screen bg-slate-100/70 dark:bg-ink-900"
      style={
        {
          "--shell-header-h": `${HEADER_H}px`,
          "--shell-navbar-w": `${navbarWidth}px`,
          "--shell-padding": `${SHELL_PADDING}px`,
          "--navbar-anim-ms": navbarAnimMs,
        } as CSSProperties
      }
    >
      <AppShellHeader user={user} mobileNavOpen={mobileOpen} onMobileToggle={() => setMobileOpen((v) => !v)} onLogout={() => void handleLogout()} />

      {/* Backdrop del navbar en mobile (overlay) · clic afuera cierra. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 md:hidden"
          style={{ top: "var(--shell-header-h)", zIndex: 190 }}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <AppShellNavbar
        roles={roles}
        permisos={permisos}
        pathname={pathname}
        mobileOpen={mobileOpen}
        onItemClick={() => setMobileOpen(false)}
        colapsado={!desktopExpanded}
        onMouseEnter={() => {
          // Entra el mouse → cancela un colapso pendiente y, si está colapsado, programa
          // la expansión (solo ocurre si el mouse sigue encima tras EXPANSION_DELAY_MS).
          cancelarColapso();
          if (!desktopExpanded) programarExpansion();
        }}
        onMouseLeave={() => {
          // Sale el mouse → cancela una expansión pendiente y se colapsa (con la gracia
          // mínima anti-titileo).
          cancelarExpansion();
          programarColapso();
        }}
      />

      <main
        className="flex min-h-dvh flex-col pt-[var(--shell-header-h)] transition-[padding-left] ease-[cubic-bezier(0.08,1,0.12,1)] md:pl-[var(--shell-navbar-w)]"
        style={{ transitionDuration: "var(--navbar-anim-ms, 220ms)" }}
      >
        <div className="flex-1" style={{ padding: "var(--shell-padding)" }}>{children}</div>
        <AppFooter />
      </main>
    </div>
  );
}
